import { InfoWithLabel } from "@/core/components/info-with-label";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  ConfidenceLabel,
  ManualJobMatchButton,
  PurchaseOrderField,
  AddedDate,
  ReviewSummary,
} from "@/accounts-payable/common";
import type { ApInvoice } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  isNonJobExpense,
  jobMatchStrategyLabel,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

export const InvoiceFieldsSection = ({
  invoice,
  businessId,
}: {
  invoice: ApInvoice;
  businessId: string | null;
}) => {
  const strategyLabel = jobMatchStrategyLabel(invoice.job_match_strategy);
  return (
    <Stack gap="md" fullWidth>
      {/* The job gets its own row: it's what the status is about, and its
            match action needs room to sit beside it. */}
      <Stack direction="row" justify="between" align="end" gap="md" fullWidth>
        <InfoWithLabel label="Matched job" fullWidth={false}>
          {invoice.st_job_external_id ? (
            <Stack gap="xs">
              <Stack
                direction="row"
                gap="sm"
                align="center"
                className="flex-wrap"
              >
                {/* Same link as the Invoices table — interactive-text green
                      (4.8:1), not the core link's lighter primary (3.3:1). */}
                <ServiceTitanIconLink
                  url={stJobUrl(invoice.st_job_external_id)}
                  label={`Open job #${invoice.st_job_external_id} in ServiceTitan`}
                  className="ml-0 text-lg text-interactive-text hover:text-interactive-text hover:underline"
                >
                  #{invoice.st_job_external_id}
                </ServiceTitanIconLink>
                {strategyLabel && (
                  <Typography variant="bodyW1" textColor="tertiary">
                    ({strategyLabel})
                  </Typography>
                )}
              </Stack>
              {/* Same figure as the Invoices table's job column. */}
              {invoice.job_match_confidence != null && (
                <ConfidenceLabel value={invoice.job_match_confidence} />
              )}
            </Stack>
          ) : isNonJobExpense(invoice) ? (
            /* Both non-job shapes are done; neither should read as unmatched. */
            <Stack direction="row" gap="sm" align="center">
              <Typography variant="bodyW1">Non-job expense</Typography>
              <Typography variant="bodyW1" textColor="tertiary">
                ({invoice.job_match_strategy})
              </Typography>
            </Stack>
          ) : (
            <Typography variant="bodyW1" textColor="tertiary">
              Not matched
            </Typography>
          )}
        </InfoWithLabel>
        {canManuallyMatchJob(invoice) && (
          <ManualJobMatchButton
            businessId={businessId}
            documentId={invoice.id}
            agentRunId={invoice.agent_run_id}
            label={invoice.st_job_external_id ? "Change job" : undefined}
            // Unmatched, matching is the one thing left to do — make it solid.
            variant={invoice.st_job_external_id ? "secondary" : "default"}
          />
        )}
      </Stack>
      <PurchaseOrderField document={invoice} businessId={businessId} />
      <Stack gap="none" fullWidth className="grid grid-cols-2 gap-4">
        <InfoWithLabel label="Invoice date">
          {formatApDate(invoice.invoice_date)}
        </InfoWithLabel>
        <InfoWithLabel label="Due date">
          {formatApDate(invoice.due_date)}
        </InfoWithLabel>
        <InfoWithLabel label="Added">
          <AddedDate value={invoice.created_at} withTime />
        </InfoWithLabel>
        <InfoWithLabel label="Review">
          <ReviewSummary document={invoice} />
        </InfoWithLabel>
        <InfoWithLabel label="Total">
          <Typography
            variant="bodyW1"
            textColor={invoice.total_amount_cents < 0 ? "negative" : "primary"}
          >
            {formatCentsOrDashIfZero(invoice.total_amount_cents)}
          </Typography>
        </InfoWithLabel>
        <InfoWithLabel label="Vendor PO field">
          {invoice.po_number ?? "—"}
        </InfoWithLabel>
        <InfoWithLabel label="Service address">
          {invoice.service_address ?? "—"}
        </InfoWithLabel>
      </Stack>
    </Stack>
  );
};
